// Profile pictures, stored by the API per person (GET/PUT/DELETE /persons/{guid}/picture). Anyone signed in can
// see a person's picture, so the same hooks serve the account menu and group members.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useMe } from '@/auth/me';

const pictureKey = (personGuid: string) => ['person-picture', personGuid] as const;

/** The signed-in user's person GUID, from GET /me. Undefined while loading, with sign-in off, or if not linked. */
export function useMyPersonGuid(): string | undefined {
  return useMe().data?.personGuid;
}

/** The query for a person's picture (a data URL, or null when they have none), shared by every avatar. */
export function personPictureQuery(personGuid: string | null | undefined) {
  return {
    queryKey: pictureKey(personGuid ?? ''),
    enabled: !!personGuid,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const response = await apiFetch(`/persons/${personGuid}/picture`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`GET picture: ${response.status}`);
      return readAsDataUrl(await response.blob());
    },
  };
}

/** A person's picture as a data URL, or null when they have none. Undefined while loading or without a GUID. */
export function usePersonPicture(personGuid: string | null | undefined): string | null | undefined {
  return useQuery(personPictureQuery(personGuid)).data;
}

/** Upload (a File, resized first) or remove (null) a person's picture; the cached picture is refreshed after. */
export function useSetPersonPicture(personGuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File | null) => {
      if (!personGuid) throw new Error('Not linked to a person.');
      const path = `/persons/${personGuid}/picture`;
      let response: Response;
      if (file) {
        const body = new FormData();
        body.append('file', await resizePicture(file), 'picture.jpg');
        response = await apiFetch(path, { method: 'PUT', body });
      } else {
        response = await apiFetch(path, { method: 'DELETE' });
      }
      if (!response.ok) throw new Error(`Saving the picture failed: ${response.status}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pictureKey(personGuid ?? '') }),
  });
}

/** Size of the stored square picture, enough for a sharp avatar on high-density screens. */
const PICTURE_SIZE = 512;

/** Crops the image to a centred square, scales it to PICTURE_SIZE and encodes it as JPEG. */
export async function resizePicture(file: File): Promise<Blob> {
  const image = await createImageBitmap(file);
  const side = Math.min(image.width, image.height);
  const size = Math.min(PICTURE_SIZE, side);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is not available.');
  context.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, size, size);
  image.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Encoding failed.'))),
      'image/jpeg',
      0.88,
    ),
  );
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
